import { useState } from 'react';
import { trpc } from '../../api/trpc';
import { useAuth } from '../../hooks/useAuth';
import { useDialogClose } from '../../hooks/useDialogClose';

interface Participant {
  userId: string;
  role: string;
  user: { id: string; name: string; email: string };
}

interface Props {
  projectId: string;
  participants: Participant[];
  onClose: () => void;
}

export function ParticipantManagementDialog({ projectId, participants, onClose }: Props) {
  const { isClosing, handleClose } = useDialogClose(onClose);
  const { userId } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [addRole, setAddRole] = useState<'member' | 'owner'>('member');
  const [showRoleHelp, setShowRoleHelp] = useState(false);

  const searchUsers = trpc.conversations.searchUsers.useQuery(
    { query: searchQuery },
    { enabled: searchQuery.length > 0 },
  );
  const addParticipant = trpc.projects.addParticipant.useMutation();
  const removeParticipant = trpc.projects.removeParticipant.useMutation();
  const updateRole = trpc.projects.updateParticipantRole.useMutation();
  const utils = trpc.useUtils();

  const participantIds = new Set(participants.map(p => p.user.id));

  const handleAdd = async (userId: string) => {
    await addParticipant.mutateAsync({ projectId, userId, role: addRole });
    void utils.projects.get.invalidate({ projectId });
    setSearchQuery('');
  };

  const handleRemove = async (userId: string) => {
    await removeParticipant.mutateAsync({ projectId, userId });
    void utils.projects.get.invalidate({ projectId });
  };

  const handleRoleChange = async (userId: string, role: 'owner' | 'member') => {
    await updateRole.mutateAsync({ projectId, userId, role });
    void utils.projects.get.invalidate({ projectId });
  };

  return (
    <div className={`delete-dialog-overlay${isClosing ? ' overlay-closing' : ''}`} onClick={handleClose}>
      <div className={`proj-dialog${isClosing ? ' dialog-closing' : ''}`} onClick={e => e.stopPropagation()} style={{ width: 440 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <span className="proj-dialog-title" style={{ marginBottom: 0 }}>Manage Participants</span>
          <div style={{ position: 'relative' }}>
            <button
              onMouseEnter={() => setShowRoleHelp(true)}
              onMouseLeave={() => setShowRoleHelp(false)}
              onClick={() => setShowRoleHelp(v => !v)}
              style={{
                width: 18, height: 18, borderRadius: '50%', border: '1px solid var(--color-border)',
                background: 'none', color: 'var(--color-text-muted)',
                fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}
            >
              ?
            </button>
            {showRoleHelp && (
              <div style={{
                position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                marginTop: 6, width: 280, padding: '10px 12px', borderRadius: 'var(--radius)',
                border: '1px solid var(--color-border)', background: 'var(--color-surface)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)', fontSize: 12, lineHeight: 1.6,
                color: 'var(--color-text-secondary)', zIndex: 10,
              }}>
                <div><strong>Owner</strong> — Full access. Can edit project settings, instructions, manage members, and archive or delete the project.</div>
                <div style={{ marginTop: 4 }}><strong>Member</strong> — Can participate in chats and upload files. Cannot modify project settings, instructions, or manage members.</div>
              </div>
            )}
          </div>
        </div>

        {/* Current participants */}
        <div style={{ marginBottom: 16 }}>
          {participants.map(p => (
            <div key={p.user.id} className="participant-row">
              <div style={{ flex: 1 }}>
                <div className="participant-name">{p.user.name}</div>
                <div className="participant-email">{p.user.email}</div>
              </div>
              <select
                value={p.role}
                onChange={e => void handleRoleChange(p.user.id, e.target.value as 'owner' | 'member')}
                style={{
                  padding: '2px 6px', fontSize: 11, border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)', background: 'var(--color-surface)',
                  color: 'var(--color-text-primary)', cursor: 'pointer', marginRight: 8,
                }}
              >
                <option value="owner">Owner</option>
                <option value="member">Member</option>
              </select>
              {p.user.id !== userId && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => void handleRemove(p.user.id)}
                  style={{ padding: '2px 8px', fontSize: 11 }}
                  title="Remove from project"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Add participant */}
        <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
            Add participant
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                flex: 1, padding: '8px 12px', border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)', background: 'var(--color-surface)',
                color: 'var(--color-text-primary)', fontFamily: 'var(--font-sans)', fontSize: 13,
                outline: 'none',
              }}
            />
            <select
              value={addRole}
              onChange={e => setAddRole(e.target.value as 'member' | 'owner')}
              style={{
                padding: '6px 8px', fontSize: 12, border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)', background: 'var(--color-surface)',
                color: 'var(--color-text-primary)', cursor: 'pointer',
              }}
            >
              <option value="editor">Member</option>
              <option value="owner">Owner</option>
            </select>
          </div>

          {/* Search results */}
          {searchQuery && (
            <div style={{ maxHeight: 160, overflowY: 'auto' }}>
              {searchUsers.data?.length === 0 && (
                <div style={{ padding: '8px 0', color: 'var(--color-text-muted)', fontSize: 12 }}>
                  No users found
                </div>
              )}
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {searchUsers.data?.filter((u: any) => !participantIds.has(u.id)).map((user: any) => (
                <button
                  key={user.id}
                  onClick={() => void handleAdd(user.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    padding: '6px 8px', border: 'none', background: 'none', cursor: 'pointer',
                    borderRadius: 'var(--radius)', fontFamily: 'var(--font-sans)', fontSize: 13,
                    color: 'var(--color-text-primary)', textAlign: 'left',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bg-hover)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  <span style={{ fontWeight: 500 }}>{user.name}</span>
                  <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{user.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="proj-dialog-actions">
          <button className="btn btn-primary" onClick={handleClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
